import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import Sidebar from "./Sidebar";
import { getPopoverByName } from "../../../utils";
import { selectSubTab } from "../../../store/common/uiSlice";
import {
  faArrowAltCircleLeft,
  faArrowAltCircleRight,
  faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  setCurrentCollection,
  setImages,
} from "../../../store/media/mediaSlice";
import {
  addBackgroundImage,
  addImage,
  generateCanvasImage,
} from "../../../canvasUtils";
import { CanvasContext } from "../../editor/CanvasContext";
import Loader from "../Loader";
import MediaMenu from "../../media/MediaMenu";

const MediaPopover = ({ type }) => {
  const isBackground = useMemo(
    () => type.toLowerCase() === "background",
    [type]
  );
  const dispatch = useDispatch();
  const trial = useSelector((state) => state?.user?.details?.data?.trial);
  const thisPopover = getPopoverByName(
    isBackground ? "Background" : "Media",
    trial
  );

  const subTab = useSelector((state) => state.ui.subTab);
  const appState = useSelector((state) => state.appState);
  const { canvas } = useContext(CanvasContext);
  const {
    collections,
    currentCollection,
    images: allImages,
    total,
  } = useSelector((state) => state.media);
  const selectedTab = thisPopover.tabs[subTab].name;
  const [searchValue, setSearchValue] = useState(""); // State to hold input value
  const [openMediaMenuIndex, setOpenMediaMenuIndex] = useState();
  const [page, setPage] = useState(1);

  const onTabSelect = useCallback(
    (tabIndex) => {
      setPage(1);
      setSearchValue("");
      dispatch(setImages([]));
      dispatch(selectSubTab(tabIndex));
    },
    [dispatch]
  );

  const onLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const onCollectionSelect = (collectionId, index) => {
    dispatch(setCurrentCollection(index));
    dispatch(thisPopover.tabs[subTab].images(collectionId));
  };

  useEffect(() => {
    onTabSelect(0);
  }, [onTabSelect]);

  useEffect(() => {
    dispatch(thisPopover.tabs[subTab].images(searchValue, page));
  }, [page, subTab, searchValue]);

  const scrollContainer = useRef(null);

  const scrollLeft = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: -150, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollBy({ left: 150, behavior: "smooth" });
    }
  };

  const handleMediaClick = async (url) => {
    const activeFunction = isBackground ? addBackgroundImage : addImage;
    await activeFunction(canvas, { url }, dispatch);
    return dispatch(generateCanvasImage(canvas));
  };

  const isMyMediaCollection = useMemo(
    () => selectedTab === "My Images",
    [selectedTab]
  );

  const handleSlideMenu = (index) => {
    return setOpenMediaMenuIndex((prev) =>
      index !== undefined ? (prev === index ? undefined : index) : undefined
    );
  };

  return (
    <div
      className="flex w-full h-full"
      onClick={(e) => {
        e.stopPropagation();
        handleSlideMenu();
      }}
    >
      {appState === "imageLoading" && <Loader />}
      <Sidebar
        titleIcon={"/tool/dashboard/static/images/icons/tools/media.svg"}
        title={type}
        tabs={thisPopover.tabs}
        onTabSelect={onTabSelect}
        uploadButton={isMyMediaCollection && thisPopover.uploadButton}
      />
      <div className="p-4 w-4/5 bg-gray-100">
        {!(
          (subTab === 2 && isBackground) ||
          (!isBackground && subTab === 3)
        ) && (
          <div className="mb-2 flex justify-between items-center">
            <input
              key={subTab}
              type="text"
              placeholder={`Search ${selectedTab}`}
              className="w-full p-2 border rounded-lg"
              onBlur={(e) => {
                if (searchValue !== e.target.value) {
                  setPage(1);
                  setSearchValue(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (searchValue !== e.target.value) {
                  setPage(1);
                  if (e.key === "Enter" && e.target.value) {
                    setSearchValue(e.target.value);
                  }
                }
              }}
            />
          </div>
        )}
        {collections && collections.length > 0 && (
          <div className="mb-3 flex items-center">
            <span
              className="text-gray-400 cursor-pointer mr-2"
              onClick={scrollLeft}
            >
              <FontAwesomeIcon
                icon={faArrowAltCircleLeft}
                className="mr-2 text-gray-800 hover:text-vb-pink"
              />
            </span>
            <div
              ref={scrollContainer}
              className="flex overflow-x-auto scroll-container space-x-4 items-center"
            >
              {collections.map(({ title, id }, index) => (
                <button
                  key={title + id}
                  className={`${
                    "whitespace-nowrap min-w-min px-2 py-1 text-xs text-vb-label-gray2 rounded-full hover:pointer " +
                    (currentCollection === index ? "bg-gray-200" : "")
                  }`}
                  onClick={() => onCollectionSelect(id, index)}
                >
                  {title}
                </button>
              ))}
            </div>

            <span
              className="text-gray-400 cursor-pointer ml-2 "
              onClick={scrollRight}
            >
              <FontAwesomeIcon
                icon={faArrowAltCircleRight}
                className="mr-2 text-gray-800 hover:text-vb-pink"
              />
            </span>
          </div>
        )}
        {allImages.length === 0 ? (
          <div className="flex flex-grow h-4/5 justify-center text-sm text-gray-500 items-center text-center w-full">
            {" "}
            No images to show.{" "}
          </div>
        ) : (
          <div className="h-[calc(100%_-_50px)] overflow-y-scroll">
            <div className="grid grid-cols-5 gap-4 w-full ">
              {" "}
              {allImages.map((image, index) => (
                <div
                  key={image.id}
                  className="relative w-full rounded flex justify-center items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    return handleMediaClick(image.urls.raw || image.urls.main);
                  }}
                >
                  {isMyMediaCollection && (
                    <>
                      <FontAwesomeIcon
                        icon={faEllipsisH}
                        className="cursor-pointer bg-slides-label rounded-sm w-6 h-4  absolute top-2 right-2 text-white p-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          return handleSlideMenu(index);
                        }}
                      />
                      {openMediaMenuIndex === index && (
                        <div
                          className="h-full w-full z-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSlideMenu();
                          }}
                        >
                          <MediaMenu
                            handleClose={handleSlideMenu}
                            id={image.id}
                            slideIndex={index}
                            className="left-[50%] bottom-[50%] w-[188px] h-auto shadow"
                          />
                        </div>
                      )}
                    </>
                  )}
                  <img
                    src={image.urls.thumb}
                    alt={`${index}`}
                    className="rounded-lg max-w-full max-h-32 hover:shadow-xl"
                  />
                </div>
              ))}
            </div>
            {!isMyMediaCollection && (
              <button
                className="mt-2 bg-black text-white rounded p-2"
                disabled={total === allImages.length}
                onClick={onLoadMore}
              >
                Load More
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaPopover;
